import { NavLink, Outlet } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { useWorkspace } from '../WorkspaceContext';

/**
 * Shared shell for workspace tabs with nested subtabs.
 * Matches Manage / Front Desk visual language.
 */
const WorkspaceSectionLayout = ({
  eyebrow,
  title,
  description,
  ariaLabel,
  tabs,
  showLocation = true,
  maxWidth = 'max-w-7xl',
  headerAction = null,
  children,
}) => {
  const { activeLocation } = useWorkspace();

  return (
    <div className={`mx-auto w-full ${maxWidth} px-4 py-6 sm:px-6 sm:py-10 lg:px-8`}>
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">{eyebrow}</p>
            <h1 className="mt-2 font-display text-3xl font-light tracking-tight text-ink-900 sm:text-4xl">
              {title}
            </h1>
            {description ? (
              <p className="mt-2 max-w-2xl text-sm text-ink-700/80">{description}</p>
            ) : null}
          </div>
          {headerAction ? <div className="flex-none pt-1">{headerAction}</div> : null}
        </div>
        {showLocation && activeLocation && (
          <p className="flex min-w-0 items-center gap-2 text-sm text-ink-700/80">
            <MapPin className="h-4 w-4 flex-none text-saffron-600" />
            <span className="truncate">{activeLocation.name}</span>
          </p>
        )}
      </div>

      {tabs?.length > 0 && (
        <nav
          aria-label={ariaLabel}
          className="-mx-4 mt-6 border-b border-ink-900/10 px-4 sm:mx-0 sm:mt-8 sm:px-0"
        >
          <div className="scrollbar-hide -mb-px flex gap-0.5 overflow-x-auto overscroll-x-contain">
            {tabs.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                className={({ isActive }) =>
                  `flex-none whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors sm:px-4 ${
                    isActive
                      ? 'border-saffron-500 text-ink-900'
                      : 'border-transparent text-ink-600/70 hover:border-ink-900/20 hover:text-ink-900'
                  }`
                }
              >
                <span className="sm:hidden">{tab.shortLabel}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      )}

      <div className="mt-6 min-w-0 sm:mt-8">{children || <Outlet />}</div>
    </div>
  );
};

export default WorkspaceSectionLayout;
